import type { LocalDatabasePromotionCriteria } from "../../../_Generated/Sync.gen";
import { intersect } from "../../Generic/Arrays";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import type {
    PromotionCalculationPromotion,
    PromotionCalculationResult,
    PromotionManagerProduct,
} from "./PromotionTypes";

export class AdvancedPromotionCache {
    protected static cache: Array<{
        mixCriteria: boolean;
        criteria: Array<LocalDatabasePromotionCriteria>;
        products: Array<{
            uuid: string;
            quantity: number;
        }>;
        result: PromotionCalculationResult;
    }> = [];

    protected static compareCriteriaArrays: [
        "products",
        "brands",
        "categories",
        "families",
        "tags",
    ] = [
        "products",
        "brands",
        "categories",
        "families",
        "tags",
    ];

    protected static compareCriteriaFields: Array<keyof LocalDatabasePromotionCriteria> = [
        "buy_amount",
        "discount_amount",
        "type",
        "quantity_type",
        "buy_amount_type",
        "discount_everyday_promotions",
        "optional",
        "buy_amount_max",
        "exclude_count_quantity",
        "exclude_price_points",
        "min_quantity",
        "mixed_only",
    ];

    protected static compareCriteria(
        compare: Array<LocalDatabasePromotionCriteria>,
        cached: Array<LocalDatabasePromotionCriteria>
    ): boolean {
        for(let i = 0, l = compare.length; i < l; i++) {
            for(let j = 0, n = AdvancedPromotionCache.compareCriteriaFields.length; j < n; j++) {
                if(
                    compare[i][AdvancedPromotionCache.compareCriteriaFields[j]] !==
                    cached[i][AdvancedPromotionCache.compareCriteriaFields[j]]
                ) {
                    return false;
                }
            }

            for(let j = 0, n = AdvancedPromotionCache.compareCriteriaArrays.length; j < n; j++) {
                if(
                    compare[i][AdvancedPromotionCache.compareCriteriaArrays[j]].length !==
                    cached[i][AdvancedPromotionCache.compareCriteriaArrays[j]].length
                ) {
                    return false;
                }

                if(
                    compare[i].excludes[AdvancedPromotionCache.compareCriteriaArrays[j]].length !==
                    cached[i].excludes[AdvancedPromotionCache.compareCriteriaArrays[j]].length
                ) {
                    return false;
                }

                if(intersect(
                    compare[i].excludes[AdvancedPromotionCache.compareCriteriaArrays[j]],
                    cached[i].excludes[AdvancedPromotionCache.compareCriteriaArrays[j]]
                ).length !== compare[i].excludes[AdvancedPromotionCache.compareCriteriaArrays[j]].length) {
                    return false;
                }

                const compareSorted = compare[i][AdvancedPromotionCache.compareCriteriaArrays[j]].toSorted((c, d) => {
                    return c.uuid.localeCompare(d.uuid);
                });

                for(let k = 0, c = compareSorted.length; k < c; k++) {
                    if(
                        compareSorted[k].uuid !==
                        cached[i][AdvancedPromotionCache.compareCriteriaArrays[j]][k].uuid
                    ) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    public static getResult(
        promotion: PromotionCalculationPromotion,
        compareProducts: Array<PromotionManagerProduct>
    ): null | PromotionCalculationResult {
        const products = compareProducts.toSorted((a, b) => a.uuid.localeCompare(b.uuid));

        for(let i = 0, l = AdvancedPromotionCache.cache.length; i < l; i++) {
            if(promotion.mix_criteria !== AdvancedPromotionCache.cache[i].mixCriteria) {
                continue;
            }

            if(promotion.criteria.length !== AdvancedPromotionCache.cache[i].criteria.length) {
                continue;
            }

            if(products.length !== AdvancedPromotionCache.cache[i].products.length) {
                continue;
            }

            let productsFound = true;

            for(let k = 0, n = products.length; k < n; k++) {
                if(products[k].uuid !== AdvancedPromotionCache.cache[i].products[k].uuid) {
                    productsFound = false;
                    break;
                }

                if(products[k].quantity !== AdvancedPromotionCache.cache[i].products[k].quantity) {
                    productsFound = false;
                    break;
                }
            }

            if(!productsFound) {
                continue;
            }

            if(AdvancedPromotionCache.compareCriteria(promotion.criteria, AdvancedPromotionCache.cache[i].criteria)) {
                return AdvancedPromotionCache.cache[i].result;
            }
        }

        return null;
    }

    public static storeResult(
        promotion: PromotionCalculationPromotion,
        products: Array<PromotionManagerProduct>,
        result: PromotionCalculationResult
    ): void {
        this.cache.push({
            mixCriteria: promotion.mix_criteria,
            criteria   : promotion.criteria.map(criteria => {
                const newCriteria = { ...criteria };

                for(const key of AdvancedPromotionCache.compareCriteriaArrays) {
                    newCriteria[key] = newCriteria[key].toSorted((a, b) => {
                        return a.uuid.localeCompare(b.uuid);
                    });
                }

                return newCriteria;
            }),
            products: products.map(product => ({
                uuid    : product.uuid,
                quantity: product.quantity,
            })).sort((a, b) => a.uuid.localeCompare(b.uuid)),
            result,
        });
    }

    public static clear(): void {
        ArrayPool.Release(AdvancedPromotionCache.cache);
        AdvancedPromotionCache.cache = ArrayPool.Get();
    }
}
